import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../redux/store";
import { QuotationsState } from "../types/quotations/QuotationsState";
import { getQuotationById, getQuotationExtras, GetQuotationRequestDto, getQuotationsByOrgId, GetQuotationsRequestDto, addQuotationExtra, AddQuotationExtraRequestDto, deleteQuotationExtra, DeleteQuotationExtraRequestDto } from "../services/QuotationsServices";
import { sendGAEvent } from "../services/AnalyticsService";
import { ADD_QUOTATION_EXTRA_CLICK, REMOVE_QUOTATION_EXTRA_CLICK } from "../utils/analytics";

const initialState: QuotationsState = {
    Quotations: [],
    QuotationsStatus: "idle",
    SelectedQuotation: undefined,
    SelectedQuotationStatus: "succeeded",
    SelectedQuotationExtras: [],
    SelectedQuotationExtrasStatus: "idle",
    AddQuotationExtrasStatus: "idle",
    DeleteQuotationExtrasStatus: "idle"
};

export const quotationsSlice = createSlice({
    name: "quotationsSlice",
    initialState,
    reducers: {
    },
    extraReducers: (builder) => {
        builder
            .addCase(getQuotationsThunk.fulfilled, (state, action) => {
                state.Quotations = action.payload;
                state.QuotationsStatus = "succeeded";
            })
            .addCase(getQuotationsThunk.pending, (state, _action) => {
                state.QuotationsStatus = "pending";
            })
            .addCase(getQuotationsThunk.rejected, (state, _action) => {
                state.QuotationsStatus = "failed";
            })
            .addCase(getQuotationThunk.fulfilled, (state, action) => {
                state.SelectedQuotation = action.payload;
                state.SelectedQuotationStatus = "succeeded";
            })
            .addCase(getQuotationThunk.pending, (state, _action) => {
                state.SelectedQuotationStatus = "pending";
            })
            .addCase(getQuotationThunk.rejected, (state, _action) => {
                state.SelectedQuotationStatus = "failed";
            })
            .addCase(getQuotationExtrasThunk.fulfilled, (state, action) => {
                state.SelectedQuotationExtras = action.payload;
                state.SelectedQuotationExtrasStatus = "succeeded";
            })
            .addCase(getQuotationExtrasThunk.pending, (state, _action) => {
                state.SelectedQuotationExtrasStatus = "pending";
            })
            .addCase(getQuotationExtrasThunk.rejected, (state, _action) => {
                state.SelectedQuotationExtrasStatus = "failed";
            })
            .addCase(addQuotationExtraThunk.fulfilled, (state, action) => {
                state.AddQuotationExtrasStatus = "succeeded";

                const { extraServiceId, id } = action.payload;
                state.SelectedQuotationExtras = state.SelectedQuotationExtras.map(extra => {
                    // If the extra's extraServiceId atches, update its properties
                    if (extra.extraServiceId === extraServiceId) {
                        return {
                            ...extra,
                            selectedByCustomer: true,
                            quotationExtraId: id
                        };
                    }
                    return extra;
                });
            })
            .addCase(addQuotationExtraThunk.pending, (state, _action) => {
                state.AddQuotationExtrasStatus = "pending";
            })
            .addCase(addQuotationExtraThunk.rejected, (state, _action) => {
                state.AddQuotationExtrasStatus = "failed";
            })
            .addCase(deleteQuotationExtraThunk.fulfilled, (state, action) => {
                state.DeleteQuotationExtrasStatus = "succeeded";

                const deletedQuotationExtraIds: number[] = action.payload;

                state.SelectedQuotationExtras = state.SelectedQuotationExtras.map(extra => {
                    if (extra.quotationExtraId === deletedQuotationExtraIds[0]) {
                        return {
                            ...extra,
                            selectedByCustomer: false,
                            quotationExtraId: undefined
                        };
                    }
                    return extra;
                });
            })
            .addCase(deleteQuotationExtraThunk.pending, (state, _action) => {
                state.DeleteQuotationExtrasStatus = "pending";
            })
            .addCase(deleteQuotationExtraThunk.rejected, (state, _action) => {
                state.DeleteQuotationExtrasStatus = "failed";
            });
    }
});

export const getQuotationsThunk = createAsyncThunk(
    "quotations/getQuotations",
    async (requestData: GetQuotationsRequestDto) => {
        return getQuotationsByOrgId(requestData);
    }
);

export const getQuotationThunk = createAsyncThunk(
    "quotations/getQuotation",
    async (requestData: GetQuotationRequestDto) => {
        return getQuotationById(requestData);
    }
);

export const getQuotationExtrasThunk = createAsyncThunk(
    "quotation/getQuotationExtras",
    async (requestData: GetQuotationRequestDto) => {
        return getQuotationExtras(requestData);
    }
)

export const addQuotationExtraThunk = createAsyncThunk(
    "quotation/addQuotationExtra",
    async (requestData: AddQuotationExtraRequestDto) => {
        sendGAEvent(ADD_QUOTATION_EXTRA_CLICK, requestData.updatedExtraId.toString());
        return addQuotationExtra(requestData);
    }
)

export const deleteQuotationExtraThunk = createAsyncThunk(
    "quotation/deleteQuotationExtra",
    async (requestData: DeleteQuotationExtraRequestDto) => {
        sendGAEvent(REMOVE_QUOTATION_EXTRA_CLICK, requestData.quotationExtraId.toString());
        return deleteQuotationExtra(requestData);
    }
)

export const quotationsSelector = (state: RootState) => state.Quotations;

export default quotationsSlice.reducer;